import { type Editor } from '@tiptap/react'
import { clsx } from 'clsx'
import {
  Bold,
  Code,
  Heading2,
  Italic,
  Link as LinkIcon,
  type LucideIcon,
  MessageSquarePlus,
  Strikethrough,
  Unlink,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useI18n } from '@/i18n/i18n'
import { type MessageKey } from '@/i18n/messages'
import { isComposing } from '@/lib/ime'
import { useBoardCtx } from '../context'

// Over selected text in a card: formatting, a link, and "say something about
// this". One bar, so the two never stack. Its own component so its state
// never re-renders the editor; it follows the selection on screen while it is
// shown (the board can pan under it).

type Mark = 'bold' | 'italic' | 'strike' | 'code'

type Shown = {
  x: number
  y: number
  active: Record<Mark | 'link' | 'heading', boolean>
  /** In the first line, the title: it stays a heading. */
  title: boolean
}

const MARKS: { mark: Mark; icon: LucideIcon; label: MessageKey; keys: string }[] = [
  { mark: 'bold', icon: Bold, label: 'format.bold', keys: '⌘B' },
  { mark: 'italic', icon: Italic, label: 'format.italic', keys: '⌘I' },
  { mark: 'strike', icon: Strikethrough, label: 'format.strike', keys: '⌘⇧S' },
  { mark: 'code', icon: Code, label: 'format.code', keys: '⌘E' },
]

function toggle(editor: Editor, mark: Mark) {
  const c = editor.chain().focus()
  if (mark === 'bold') c.toggleBold().run()
  else if (mark === 'italic') c.toggleItalic().run()
  else if (mark === 'strike') c.toggleStrike().run()
  else c.toggleCode().run()
}

export function SelectionBar({ editor, cardId }: { editor: Editor; cardId: string }) {
  const { actions, engine } = useBoardCtx()
  const { t } = useI18n()
  const [at, setAt] = useState<Shown | null>(null)
  const [linking, setLinking] = useState<string | null>(null)
  const linkInput = useRef<HTMLInputElement>(null)
  const linkingNow = useRef(false)
  linkingNow.current = linking !== null

  // Shown once a selection settles (the mouse or the selecting keys are
  // released), hidden while selecting or typing, and kept beside the text as
  // the board pans or zooms. The editor learns about a keyboard selection
  // from the browser asynchronously (selectionchange), possibly after the key
  // is released: so whenever nothing is held, every selection update counts.
  useEffect(() => {
    // The editor's DOM can be replaced after this runs (the view mounts with
    // its React node): listen on the document and ask at event time.
    const inEditor = (e: Event) => {
      if (editor.isDestroyed) return false
      return e.target instanceof Node && editor.view.dom.contains(e.target)
    }
    let shown = false
    /** A mouse button or a key went down in the editor and is not released yet. */
    let held = false
    const place = () => {
      if (editor.isDestroyed) return
      const { from, to, empty } = editor.state.selection
      if (empty || !editor.isFocused || !editor.state.doc.textBetween(from, to, ' ').trim()) {
        hide()
        return
      }
      const end = editor.view.coordsAtPos(to)
      const start = editor.view.coordsAtPos(from)
      shown = true
      const active = {
        bold: editor.isActive('bold'),
        italic: editor.isActive('italic'),
        strike: editor.isActive('strike'),
        code: editor.isActive('code'),
        link: editor.isActive('link'),
        heading: editor.isActive('heading'),
      }
      const title = editor.state.selection.$from.index(0) === 0
      setAt((cur) => {
        const next = {
          x: Math.round(Math.min(start.left, end.left)),
          y: Math.round(Math.min(start.top, end.top)),
          active,
          title,
        }
        return cur &&
          cur.x === next.x &&
          cur.y === next.y &&
          cur.title === title &&
          JSON.stringify(cur.active) === JSON.stringify(active)
          ? cur
          : next
      })
    }
    const hide = () => {
      // Typing an address keeps the bar: focus is in its input, not the editor.
      if (linkingNow.current) return
      shown = false
      setAt(null)
    }
    const onDown = (e: Event) => {
      if (!inEditor(e)) return
      held = true
      if (shown) hide()
    }
    const onMouseUp = () => {
      // Wherever it is released: a drag-select can end outside the card.
      if (!held) return
      held = false
      place()
    }
    const onKeyUp = (e: KeyboardEvent) => {
      // While Shift is down the selection is still being extended.
      if (!held || e.shiftKey) return
      held = false
      place()
    }
    const onSelection = () => {
      if (!held) place()
    }
    const onBlur = () => {
      held = false
      hide()
    }
    const onViewport = () => {
      if (shown) place()
    }
    const offViewport = engine.current?.onViewportChange(onViewport)
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onDown)
    document.addEventListener('mouseup', onMouseUp)
    document.addEventListener('keyup', onKeyUp)
    editor.on('selectionUpdate', onSelection)
    editor.on('transaction', onSelection)
    editor.on('blur', onBlur)
    window.addEventListener('resize', onViewport)
    return () => {
      offViewport?.()
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onDown)
      document.removeEventListener('mouseup', onMouseUp)
      document.removeEventListener('keyup', onKeyUp)
      editor.off('selectionUpdate', onSelection)
      editor.off('transaction', onSelection)
      editor.off('blur', onBlur)
      window.removeEventListener('resize', onViewport)
    }
  }, [editor, engine])

  useEffect(() => {
    if (linking !== null) linkInput.current?.focus()
  }, [linking])

  if (!at) return null

  const endLink = (href: string | null) => {
    setLinking(null)
    const c = editor.chain().focus()
    if (href === null) c.run()
    else if (!href.trim()) c.extendMarkRange('link').unsetLink().run()
    else {
      const url = /^[a-z][\w+.-]*:/i.test(href.trim()) ? href.trim() : `https://${href.trim()}`
      c.extendMarkRange('link').setLink({ href: url }).run()
    }
  }

  const button = (key: string, Icon: LucideIcon, label: string, on: boolean, run: () => void) => (
    <button
      key={key}
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={on}
      onClick={run}
      className={clsx(
        'inline-flex h-7 w-7 items-center justify-center rounded-md hover:bg-surface-2 hover:text-ink',
        on ? 'text-(--ai)' : 'text-ink-2',
      )}
    >
      <Icon size={14} />
    </button>
  )

  return createPortal(
    <div
      role="toolbar"
      aria-label={t('format.toolbar')}
      data-selection-bar=""
      // Keep the selection: a click must not take focus from the editor first.
      onMouseDown={(e) => {
        if (!(e.target instanceof HTMLInputElement)) e.preventDefault()
      }}
      className="fixed z-50 flex h-9 -translate-y-full items-center gap-0.5 rounded-lg border border-line bg-surface px-1 text-[12px] shadow-md"
      style={{ left: at.x, top: at.y - 6 }}
    >
      {linking !== null ? (
        <>
          <input
            ref={linkInput}
            value={linking}
            placeholder={t('format.linkPlaceholder')}
            aria-label={t('format.link')}
            onChange={(e) => {
              setLinking(e.target.value)
            }}
            onKeyDown={(e) => {
              if (isComposing(e)) return
              if (e.key === 'Enter') {
                e.preventDefault()
                endLink(linking)
              } else if (e.key === 'Escape') {
                e.preventDefault()
                endLink(null)
              }
            }}
            onBlur={() => {
              if (linkingNow.current) endLink(null)
            }}
            className="h-7 w-56 bg-transparent px-2 text-[13px] outline-none placeholder:text-muted"
          />
          {at.active.link &&
            button('unlink', Unlink, t('format.linkRemove'), false, () => {
              endLink('')
            })}
        </>
      ) : (
        <>
          {MARKS.map((m) =>
            button(m.mark, m.icon, `${t(m.label)} ${m.keys}`, at.active[m.mark], () => {
              toggle(editor, m.mark)
            }),
          )}
          {button('link', LinkIcon, t('format.link'), at.active.link, () => {
            setLinking((editor.getAttributes('link').href as string | undefined) ?? '')
          })}
          {!at.title &&
            button('heading', Heading2, t('format.heading'), at.active.heading, () => {
              editor.chain().focus().toggleHeading({ level: 2 }).run()
            })}
          <span className="mx-1 h-4 w-px bg-line" />
          <button
            type="button"
            data-quote-bubble=""
            onClick={() => {
              const { from, to } = editor.state.selection
              const quote = editor.state.doc.textBetween(from, to, ' ').replace(/\s+/g, ' ').trim()
              if (!quote) return
              const y = engine.current?.screenToBoard({ x: at.x, y: at.y }).y
              editor.commands.blur()
              actions.current?.sayAbout({ items: [cardId], quote }, y)
            }}
            className="inline-flex h-7 items-center gap-1.5 rounded-md px-2 font-medium text-ink-2 hover:bg-surface-2 hover:text-ink"
          >
            <MessageSquarePlus size={13} />
            {t('anchor.sayAboutQuote')}
          </button>
        </>
      )}
    </div>,
    document.body,
  )
}
